import type { LiveDebugLogger } from '@/app/[locale]/vidu-stream/live-debug-log';
import { sanitizeString } from '@/app/[locale]/vidu-stream/live-debug-log';
import {
  buildWsUrl,
  CONN_INIT_ERROR_NOT_READY,
  CONN_INIT_RETRY_INTERVAL_MS,
  type LiveConfigRateLimitAckPayload,
  type LiveErrorPayload,
  type LiveMediaStatusPayload,
  type LiveTextInputAckPayload,
  type LiveUpdateConfigAckPayload,
  type LiveUpdateConfigPayload,
  SIGNAL_TYPE_CALL_HANGUP,
  SIGNAL_TYPE_CONN_INIT,
  SIGNAL_TYPE_CONN_INIT_ACK,
  SIGNAL_TYPE_ERROR,
  SIGNAL_TYPE_FORCE_HANGUP,
  SIGNAL_TYPE_LIVE_CONFIG_RATE_LIMIT_ACK,
  SIGNAL_TYPE_MEDIA_STATUS,
  SIGNAL_TYPE_TEXT_INPUT,
  SIGNAL_TYPE_TEXT_INPUT_ACK,
  SIGNAL_TYPE_TIMEOUT_HANGUP,
  SIGNAL_TYPE_UPDATE_LIVE_CONFIG,
  SIGNAL_TYPE_UPDATE_LIVE_CONFIG_ACK,
  type WsSignal,
} from './live-call';

interface LiveWsSessionParams {
  accessToken?: string;
  buildUrl?: (liveId: string) => string;
  liveId: string;
  logger?: LiveDebugLogger;
  rtcUserId: string;
  onBeforeSocketReplace?: () => void;
  onConnInitAckReady: () => void;
  onConnInitAckFailure: (payload: { errorCode?: string; errorMessage?: string }) => void;
  onConnInitAckNotReady?: () => void;
  onConnectionOpen: () => void;
  onConnectionError: () => void;
  onConnectionClosed: () => void;
  onServerForceHangup: (payload: { signalType: number; rawReason?: string }) => void;
  /** 收到用户文本输入回执（text_input_ack=14）。 */
  onTextInputAck?: (payload: LiveTextInputAckPayload) => void;
  /** 收到服务端错误信令（error=15）。 */
  onServerError?: (payload: LiveErrorPayload) => void;
  onLiveConfigRateLimit?: (payload: LiveConfigRateLimitAckPayload) => void;
  onUpdateLiveConfigAck?: (payload: LiveUpdateConfigAckPayload) => void;
  scheduleTimer?: (callback: () => void, delayMs: number) => number;
  clearTimer?: (timerId: number) => void;
  webSocketFactory?: (url: string) => WebSocket;
}

interface SendHangupOptions {
  reason: string;
}

interface SendMediaStatusOptions {
  payload: LiveMediaStatusPayload;
}

interface SendTextInputOptions {
  /** 客户端生成的稳定去重 / 收口 id，回填在 error(msg_id) / 后续关联信号里。 */
  clientMsgId: string;
  text: string;
}

interface SendUpdateLiveConfigOptions {
  payload: LiveUpdateConfigPayload;
}

export interface SendTextInputResult {
  clientMsgId: string;
  socketState: number | undefined;
  sent: boolean;
}

interface CloseOptions {
  sendHangup?: boolean;
  hangupReason?: string;
}

export interface SendHangupResult {
  reason: string;
  socketState: number | undefined;
  sent: boolean;
}

export class LiveWsSession {
  private readonly clearTimerFn: (timerId: number) => void;
  private readonly buildUrl: (liveId: string) => string;
  private readonly liveId: string;
  private readonly logger?: LiveDebugLogger;
  private readonly onBeforeSocketReplace?: () => void;
  private readonly onConnInitAckFailure: LiveWsSessionParams['onConnInitAckFailure'];
  private readonly onConnInitAckNotReady?: LiveWsSessionParams['onConnInitAckNotReady'];
  private readonly onConnInitAckReady: LiveWsSessionParams['onConnInitAckReady'];
  private readonly onConnectionClosed: LiveWsSessionParams['onConnectionClosed'];
  private readonly onConnectionError: LiveWsSessionParams['onConnectionError'];
  private readonly onConnectionOpen: LiveWsSessionParams['onConnectionOpen'];
  private readonly onServerForceHangup: LiveWsSessionParams['onServerForceHangup'];
  private readonly onTextInputAck: NonNullable<LiveWsSessionParams['onTextInputAck']>;
  private readonly onServerError: NonNullable<LiveWsSessionParams['onServerError']>;
  private readonly onLiveConfigRateLimit: NonNullable<LiveWsSessionParams['onLiveConfigRateLimit']>;
  private readonly onUpdateLiveConfigAck: NonNullable<LiveWsSessionParams['onUpdateLiveConfigAck']>;
  private readonly rtcUserId: string;
  private readonly scheduleTimerFn: (callback: () => void, delayMs: number) => number;
  private readonly webSocketFactory: (url: string) => WebSocket;

  private connId: string | null = null;
  private connInitRetryTimerId: number | null = null;
  private disposed = false;
  private pendingUpdateLiveConfig: LiveUpdateConfigPayload | null = null;
  // 当前 socket 是否已收到成功的 conn_init_ack。重连新建 socket 时归 false：
  // 握手完成前不允许发业务信令（否则会以 conn_id=null 发出并被误标 sent）。
  private connInitReady = false;
  private seqId = 0;
  private socket: WebSocket | null = null;

  constructor({
    accessToken,
    buildUrl = (id) => buildWsUrl(id, { accessToken }),
    liveId,
    logger,
    rtcUserId,
    onBeforeSocketReplace,
    onConnInitAckFailure,
    onConnInitAckNotReady,
    onConnInitAckReady,
    onConnectionClosed,
    onConnectionError,
    onConnectionOpen,
    onServerForceHangup,
    onTextInputAck = () => {},
    onServerError = () => {},
    onLiveConfigRateLimit = () => {},
    onUpdateLiveConfigAck = () => {},
    scheduleTimer = (callback, delayMs) => window.setTimeout(callback, delayMs),
    clearTimer = (timerId) => window.clearTimeout(timerId),
    webSocketFactory = (url) => new WebSocket(url),
  }: LiveWsSessionParams) {
    this.buildUrl = buildUrl;
    this.liveId = liveId;
    this.logger = logger;
    this.rtcUserId = rtcUserId;
    this.onBeforeSocketReplace = onBeforeSocketReplace;
    this.onConnInitAckFailure = onConnInitAckFailure;
    this.onConnInitAckNotReady = onConnInitAckNotReady;
    this.onConnInitAckReady = onConnInitAckReady;
    this.onConnectionClosed = onConnectionClosed;
    this.onConnectionError = onConnectionError;
    this.onConnectionOpen = onConnectionOpen;
    this.onServerForceHangup = onServerForceHangup;
    this.onTextInputAck = onTextInputAck;
    this.onServerError = onServerError;
    this.onLiveConfigRateLimit = onLiveConfigRateLimit;
    this.onUpdateLiveConfigAck = onUpdateLiveConfigAck;
    this.scheduleTimerFn = scheduleTimer;
    this.clearTimerFn = clearTimer;
    this.webSocketFactory = webSocketFactory;
  }

  connect(): void {
    if (this.disposed) {
      return;
    }

    this.onBeforeSocketReplace?.();
    this.clearConnInitRetryTimer();
    this.logger?.mark('ws_connect');
    this.logger?.log('ws', 'ws_connect');
    this.socket = this.webSocketFactory(this.buildUrl(this.liveId));
    this.connId = null;
    this.connInitReady = false;
    this.seqId = 0;

    const socket = this.socket;

    const sendConnInit = () => {
      if (this.disposed || socket.readyState !== WebSocket.OPEN) {
        return;
      }

      socket.send(
        JSON.stringify({
          seq_id: this.seqId++,
          type: SIGNAL_TYPE_CONN_INIT,
          live_id: this.liveId,
        }),
      );
    };

    socket.onopen = () => {
      if (this.disposed || this.socket !== socket) {
        return;
      }

      const elapsedMsSinceWsConnect = this.logger?.since('ws_connect');
      this.logger?.mark('ws_open');
      this.logger?.log('ws', 'ws_open', {
        elapsed_ms_since_ws_connect: elapsedMsSinceWsConnect,
        elapsed_ms_since_session_start: this.logger?.since('session_start'),
      });
      this.onConnectionOpen();
      sendConnInit();
    };

    socket.onerror = () => {
      if (this.disposed || this.socket !== socket) {
        return;
      }

      this.logger?.log('ws', 'ws_error', { ready_state: socket.readyState });
      this.onConnectionError();
    };

    socket.onclose = (event) => {
      const isCurrent = this.socket === socket;
      if (isCurrent) {
        this.socket = null;
      }

      if (this.disposed) {
        return;
      }

      this.logger?.log('ws', 'ws_close', {
        code: event.code,
        was_clean: event.wasClean,
      });
      this.onConnectionClosed();
    };

    socket.onmessage = (event) => {
      if (this.disposed || this.socket !== socket) {
        return;
      }

      try {
        const signal = JSON.parse(String(event.data)) as WsSignal;

        if (signal.conn_id) {
          this.connId = signal.conn_id;
        }

        switch (signal.type) {
          case SIGNAL_TYPE_CONN_INIT_ACK: {
            const ack = signal.payload?.conn_init_ack;

            if (!ack || typeof ack.success !== 'boolean') {
              this.logger?.log('ws', 'ws_init_ack', {
                success: false,
                error_code: 'INVALID_ACK',
                ack_present: Boolean(ack),
                success_type: typeof ack?.success,
                elapsed_ms_since_ws_open: this.logger?.since('ws_open'),
                elapsed_ms_since_ws_connect: this.logger?.since('ws_connect'),
                elapsed_ms_since_session_start: this.logger?.since('session_start'),
              });
              this.onConnInitAckFailure({ errorCode: 'INVALID_ACK' });
              break;
            }

            if (!ack.success && ack.error_code === CONN_INIT_ERROR_NOT_READY) {
              this.onConnInitAckNotReady?.();
              this.logger?.log('ws', 'ws_init_ack', {
                success: false,
                error_code: ack.error_code,
                will_retry: true,
                elapsed_ms_since_ws_open: this.logger?.since('ws_open'),
                elapsed_ms_since_ws_connect: this.logger?.since('ws_connect'),
                elapsed_ms_since_session_start: this.logger?.since('session_start'),
              });
              this.clearConnInitRetryTimer();
              this.connInitRetryTimerId = this.scheduleTimerFn(sendConnInit, CONN_INIT_RETRY_INTERVAL_MS);
              break;
            }

            if (ack && ack.success === false) {
              const errorMessage = ack.error_msg?.trim() || undefined;
              this.logger?.log('ws', 'ws_init_ack', {
                success: false,
                error_code: ack.error_code,
                error_msg_sanitized: errorMessage ? sanitizeString(errorMessage) : undefined,
                elapsed_ms_since_ws_open: this.logger?.since('ws_open'),
                elapsed_ms_since_ws_connect: this.logger?.since('ws_connect'),
                elapsed_ms_since_session_start: this.logger?.since('session_start'),
              });
              this.onConnInitAckFailure({
                errorCode: ack.error_code,
                errorMessage,
              });
              break;
            }

            this.logger?.log('ws', 'ws_init_ack', {
              success: true,
              elapsed_ms_since_ws_open: this.logger?.since('ws_open'),
              elapsed_ms_since_ws_connect: this.logger?.since('ws_connect'),
              elapsed_ms_since_session_start: this.logger?.since('session_start'),
            });
            this.connInitReady = true;
            if (this.pendingUpdateLiveConfig) {
              this.sendUpdateLiveConfig({ payload: this.pendingUpdateLiveConfig });
            }
            this.onConnInitAckReady();
            break;
          }
          case SIGNAL_TYPE_FORCE_HANGUP: {
            const rawReason = signal.payload?.hangup?.hangup_reason?.trim();
            this.logger?.log('ws', 'ws_force_hangup', {
              signal_type: signal.type,
              raw_reason_sanitized: rawReason ? sanitizeString(rawReason) : undefined,
            });
            this.onServerForceHangup({
              signalType: signal.type,
              rawReason,
            });
            break;
          }
          case SIGNAL_TYPE_TIMEOUT_HANGUP: {
            this.logger?.log('ws', 'ws_timeout_hangup', { signal_type: signal.type });
            this.onServerForceHangup({ signalType: signal.type });
            break;
          }
          case SIGNAL_TYPE_TEXT_INPUT_ACK: {
            const ack = signal.payload?.text_input_ack;
            if (ack) {
              this.onTextInputAck(ack);
            }
            break;
          }
          case SIGNAL_TYPE_ERROR: {
            const error = signal.payload?.error;
            if (error) {
              this.onServerError(error);
            }
            break;
          }
          case SIGNAL_TYPE_LIVE_CONFIG_RATE_LIMIT_ACK: {
            const ack = signal.payload?.live_config_rate_limit_ack;
            if (ack) this.onLiveConfigRateLimit(ack);
            break;
          }
          case SIGNAL_TYPE_UPDATE_LIVE_CONFIG_ACK: {
            const ack = signal.payload?.update_live_config_ack;
            if (ack) this.onUpdateLiveConfigAck(ack);
            break;
          }
          default:
            break;
        }
      } catch {
        // Ignore malformed server signals.
      }
    };
  }

  sendHangup({ reason }: SendHangupOptions): SendHangupResult {
    const socket = this.socket;
    const socketState = socket?.readyState;

    if (!socket || socket.readyState !== WebSocket.OPEN) {
      this.logger?.log('ws', 'ws_send_hangup', {
        reason: sanitizeString(reason),
        socket_state: socketState,
        sent: false,
      });
      return {
        reason,
        socketState,
        sent: false,
      };
    }

    socket.send(
      JSON.stringify({
        seq_id: this.seqId++,
        type: SIGNAL_TYPE_CALL_HANGUP,
        user_id: this.rtcUserId,
        conn_id: this.connId,
        live_id: this.liveId,
        payload: {
          hangup: {
            hangup_reason: reason,
          },
        },
      }),
    );
    this.logger?.log('ws', 'ws_send_hangup', {
      reason: sanitizeString(reason),
      socket_state: socketState,
      sent: true,
    });
    return {
      reason,
      socketState,
      sent: true,
    };
  }

  sendMediaStatus({ payload }: SendMediaStatusOptions): boolean {
    const socket = this.socket;

    if (!socket || socket.readyState !== WebSocket.OPEN) {
      return false;
    }

    socket.send(
      JSON.stringify({
        seq_id: this.seqId++,
        type: SIGNAL_TYPE_MEDIA_STATUS,
        user_id: this.rtcUserId,
        conn_id: this.connId,
        live_id: this.liveId,
        payload: {
          media_status: payload,
        },
      }),
    );
    return true;
  }

  sendUpdateLiveConfig({ payload }: SendUpdateLiveConfigOptions): boolean {
    const socket = this.socket;

    if (!socket || socket.readyState !== WebSocket.OPEN || !this.connInitReady) {
      this.pendingUpdateLiveConfig = payload;
      return false;
    }

    socket.send(
      JSON.stringify({
        seq_id: this.seqId++,
        type: SIGNAL_TYPE_UPDATE_LIVE_CONFIG,
        user_id: this.rtcUserId,
        conn_id: this.connId,
        live_id: this.liveId,
        payload: {
          update_live_config: payload,
        },
      }),
    );
    this.logger?.log('ws', 'ws_send_update_live_config', {
      client_msg_id: payload.client_msg_id,
      ...('editing' in payload.config
        ? {
            scene: payload.config.editing.scene,
            references: JSON.stringify(payload.config.editing.references),
          }
        : 'avatar' in payload.config
          ? { scene: payload.config.avatar.scene, references: JSON.stringify(payload.config.avatar.references) }
          : 'clear' in payload.config
            ? { clear: true }
            : {}),
    });
    this.pendingUpdateLiveConfig = null;
    return true;
  }

  /**
   * 发送用户文本输入（text_input=12）。发送前检查 socket 状态：
   * 非 OPEN 时不发，返回 sent=false，由上层将该消息置为 failed（重连空窗不静默丢）。
   */
  sendTextInput({ clientMsgId, text }: SendTextInputOptions): SendTextInputResult {
    const socket = this.socket;
    const socketState = socket?.readyState;

    // 需 socket OPEN 且已完成握手（conn_init_ack）才允许发：
    // 重连新 socket 可能已 OPEN 但握手未完成，此时 conn_id 仍为 null，发出去也无效。
    if (!socket || socket.readyState !== WebSocket.OPEN || !this.connInitReady) {
      return {
        clientMsgId,
        socketState,
        sent: false,
      };
    }

    try {
      socket.send(
        JSON.stringify({
          seq_id: this.seqId++,
          type: SIGNAL_TYPE_TEXT_INPUT,
          user_id: this.rtcUserId,
          conn_id: this.connId,
          live_id: this.liveId,
          payload: {
            text_input: {
              client_msg_id: clientMsgId,
              content: text,
              timestamp: Date.now(),
            },
          },
        }),
      );
    } catch {
      // OPEN 检查与 send 之间 socket 可能被关闭 / 竞态失败：视为未发出，交由上层置 failed。
      return {
        clientMsgId,
        socketState: socket.readyState,
        sent: false,
      };
    }

    return {
      clientMsgId,
      socketState,
      sent: true,
    };
  }

  close({ sendHangup = false, hangupReason }: CloseOptions = {}): SendHangupResult | null {
    this.clearConnInitRetryTimer();

    const result = sendHangup && hangupReason ? this.sendHangup({ reason: hangupReason }) : null;

    const socket = this.socket;
    this.socket = null;
    socket?.close();

    return result;
  }

  dispose(): void {
    this.disposed = true;
    this.close();
  }

  getSocketState(): number | undefined {
    return this.socket?.readyState;
  }

  private clearConnInitRetryTimer(): void {
    if (this.connInitRetryTimerId !== null) {
      this.clearTimerFn(this.connInitRetryTimerId);
      this.connInitRetryTimerId = null;
    }
  }
}
